import { useMemo } from 'react';
import { TextField } from '@zep-us/zus';
import { useTranslation } from 'next-i18next';
import { SchoolMapper } from '@/shared/category/util';
import { MobileCategorySingleSelect } from '@/views/build/features/category-select-view/components/MobileCategorySingleSelect';
import { type CategorySelectorState } from '@/views/build/features/category-selector/hooks/useCategorySelector';
import { CUSTOM_PUBLISHER_MAX_LENGTH, CUSTOM_PUBLISHER_MIN_LENGTH } from '@/views/build/form/schema/BuildPublisherInfoSchema';
import { isAllGrades } from '@/views/build/util';

/**
 * Publisher Category Mobile Selection Component
 *
 * Mobile layout for selecting publisher category.
 * Combines MobileCategorySingleSelect with TextField for custom publisher input.
 * Shows custom input field when "기타" (etc) is selected.
 */
export const PublisherMobileSelection = ({
  categoryInfo
}: {
  categoryInfo: CategorySelectorState;
}) => {
  const {
    t
  } = useTranslation(['build', 'common']);
  const publishers = categoryInfo.publisher.publishers;
  const selectedPublisher = categoryInfo.publisher.selectedPublisher;
  const customPublisher = categoryInfo.publisher.selectedCustomPublisher;
  const shoulShowPublisherSelect = useMemo(() => {
    // publisher 리스트가 없거나, 기본값(선택 안함)만 있으면 표시 X
    if (publishers == null || publishers.length <= 1) return false;
    if (!categoryInfo.schoolType.selectedSchoolTypeNode) return false;
    const selectedSchoolTypeName = categoryInfo.schoolType.selectedSchoolTypeNode?.name?.toLocaleLowerCase();
    if (selectedSchoolTypeName === 'primary' && !categoryInfo.grade.selectedGradeNode) return false;
    if (isAllGrades(categoryInfo.grade.selectedGradeNode?.groupId)) return false;
    return true;
  }, [categoryInfo.schoolType.selectedSchoolTypeNode, categoryInfo.grade.selectedGradeNode, publishers]);
  if (!shoulShowPublisherSelect) return null;
  return <>
      {/* Divider */}
      <div className="h-px w-full bg-alternative" />

      {/* Publisher Select */}
      <MobileCategorySingleSelect value={selectedPublisher?.nodeId ?? null} categoryNodes={publishers} onValueChange={value => {
      categoryInfo.publisher.setPublisher(value);
    }} title={t('build:schoolInfo.textbookPublisher')} placeholder={t('build:schoolInfo.textbookPublisher')} error={false} required={false} data-sentry-element="MobileCategorySingleSelect" data-sentry-source-file="PublisherMobileSelection.tsx" />

      {/* Custom Publisher Input - Show when "기타" is selected */}
      {SchoolMapper.isEtc(selectedPublisher?.nodeId) && <TextField.Root required={true}>
          <TextField.Input placeholder={t('build:schoolInfo.textbookName')} value={customPublisher || ''} onChange={e => {
        categoryInfo.updateCustomPublisher(e.target.value);
      }} maxLength={CUSTOM_PUBLISHER_MAX_LENGTH} minLength={CUSTOM_PUBLISHER_MIN_LENGTH} />
        </TextField.Root>}
    </>;
};